﻿import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { type BrandDto, type ProductDto, type ProductUnitOptionDto, type RetailerDto, type SubBrandDto, UnitOfMeasure } from "../../../contracts";
import { useChatConversation } from "../../../hooks/useChatConversation";
import { getAgentResponseById, type CommercialSimulatorSubBrandAgentResponse } from "../../../models/agentResponseModels";
import { isCommercialSimulatorSubBrandToolRequest, type CommercialSimulatorSubBrandToolRequest, type ProductSelection } from "../../../models/toolRequestModels";
import { getToolResponseById, type CommercialSimulatorSubBrandToolResponse } from "../../../models/toolResponseModels";
import {
    clearGenerateSimulationRequest,
    selectGenerateSimulationRequested,
    setSimulationSetupSelection
} from "../../../slices/chatSlice";
import { getUnitOfMeasureAbbreviation } from "../../../utils/unitOfMeasureUtils";
import type { ToolComponentProps } from "../toolRenderer";
import ActionButtons from "./components/actionButtons";
import BrandSelector from "./components/brandSelector";
import QuantitySelector from "./components/quantitySelector";
import RetailerSelector from "./components/retailerSelector";
import SubBrandSelector from "./components/subBrandSelector";
import { useSubBrandSetupData, type SubBrandSetupSelection } from "./hooks/useSubBrandSetupData";
import { resolveInitialSubBrandSelection } from "./utils/resolveInitialSubBrandSelection";

const getUnitLabel = (product: ProductDto): string | null => {
    const unitSize = product.unit === UnitOfMeasure.Each
        ? getUnitOfMeasureAbbreviation(product.unit)
        : product.unit_size != null
            ? `${product.unit_size}${product.unit != null ? getUnitOfMeasureAbbreviation(product.unit) : ""}`
            : null;

    const unitsPerPack = product.units_per_pack != null && product.units_per_pack > 0
        ? `${product.units_per_pack} pack`
        : null;

    return [unitSize, unitsPerPack].filter(Boolean).join(" / ") || null;
};

const getProductMeta = (product: ProductDto): string => {
    return [
        product.retailer_name,
        product.brand,
        product.sub_brand
    ].filter(Boolean).join(" | ");
};

const getIsFetching = (
    status: Record<string, { isFetching: boolean }>
): boolean => Object.values(status).some(
    (query: { isFetching: boolean }): boolean => query.isFetching
);

const getIsError = (
    status: Record<string, { isError: boolean }>
): boolean => Object.values(status).some(
    (query: { isError: boolean }): boolean => query.isError
);

const getProductIdentifiers = (product: ProductDto): string => {
    return [
        product.sku ? `SKU ${product.sku}` : null,
        product.barcode ? `Barcode ${product.barcode}` : null
    ].filter(Boolean).join(" · ");
};

const SubBrandToolCallSteps: React.FC<ToolComponentProps> = ({
    message
}: ToolComponentProps) => {
    const {
        chatMessages,
        isChatBusy,
        hasStartedSimulation,
        actions
    } = useChatConversation();

    if (message?.id == null || !isCommercialSimulatorSubBrandToolRequest(message.payload)) {
        throw new Error("Unknown message tool request type or state");
    }

    const toolRequest: CommercialSimulatorSubBrandToolRequest = message.payload;
    const [initializedToolRequestId, setInitializedToolRequestId]
        = useState<typeof toolRequest.id | null>(null);

    const startInProgressRef = useRef(false);
    const dispatch = useAppDispatch();
    const generateRequested = useAppSelector(selectGenerateSimulationRequested);

    /*
        There are two places where our selection data can come from 1. The users previous selection 2. The initial toolrequest above (the LLM)
        We first want to get any persisted user selection (i.e. what the user has selected and saved) then fallback to the tool request input (what the llm suggested).
        Finally we let the user select what they want.
    */

    const persistedUserSelection = getToolResponseById<CommercialSimulatorSubBrandToolResponse>(chatMessages, toolRequest.id)?.tool_call_user_selection;
    const subbrandAgentResponse = getAgentResponseById<CommercialSimulatorSubBrandAgentResponse>(chatMessages, toolRequest.id);

    // If both of these are available we have completed this toolcall.
    const isReadOnly = subbrandAgentResponse != null || persistedUserSelection != null;

    const [selection, setSelection] = useState<SubBrandSetupSelection>({
        brandId: null,
        subBrandId: null,
        retailerId: null,
        packSizeKey: null
    });

    const {
        brands,
        allSubBrands,
        filteredSubBrands,
        retailers,
        quantityOptions,
        quantity,
        products,
        status
    } = useSubBrandSetupData(selection);

    const isFetching = getIsFetching(status);
    const isError = getIsError(status);

    const selectedProducts: ProductSelection[] = useMemo(
        () => isReadOnly
            ? persistedUserSelection?.products ?? []
            : products.map(product => ({
                    id: product.id,
                    retailer_id: product.retailer_id,
                    retailer_name: product.retailer_name,
                    name: product.name
                })),
        [isReadOnly, products, persistedUserSelection?.products]
    );

    const selectedBrand: BrandDto | null = brands.find((brand: BrandDto): boolean => brand.id === selection.brandId) ?? null;
    const selectedSubBrand: SubBrandDto | null = allSubBrands.find((subBrand: SubBrandDto): boolean => subBrand.id === selection.subBrandId) ?? null;
    const selectedRetailer: RetailerDto | null = retailers.find((retailer: RetailerDto): boolean => retailer.id === selection.retailerId) ?? null;
    const selectedQuantity: ProductUnitOptionDto | null = quantity;

    const isInitialized = initializedToolRequestId === toolRequest.id;

    const canStartSimulation = isInitialized
        && selectedBrand != null
        && selectedSubBrand != null
        && selectedQuantity != null
        && selectedRetailer != null
        && status.products.isSuccess
        && !status.products.isFetching
        && !status.products.isError
        && selectedProducts.length > 0
        && !isChatBusy
        && !isReadOnly
        && !hasStartedSimulation;

    const startSimulationAsync = useCallback(async (): Promise<void> => {
        if (startInProgressRef.current || !canStartSimulation) {
            return;
        }

        startInProgressRef.current = true;

        try {
            const didStart = await actions.startSubBrandSimulationAsync({
                products: selectedProducts,
                retailer_id: selectedRetailer.id,
                retailer_name: selectedRetailer.name,
                unit: selectedQuantity.unit,
                unit_size: selectedQuantity.unit_size,
                units_per_pack: selectedQuantity.units_per_pack,
                brand_id: selectedBrand.id,
                sub_brand_id: selectedSubBrand.id,
                sub_brand_name: selectedSubBrand.name,
                pack_size_key: selectedQuantity.pack_size_key
            }, message);

            if (!didStart) {
                startInProgressRef.current = false;
            }
        } catch (error: unknown) {
            startInProgressRef.current = false;
            console.error("Error starting sub-brand simulation.", error);
        }
    }, [actions, canStartSimulation, message, selectedProducts,
        selectedQuantity, selectedRetailer, selectedBrand, selectedSubBrand]);

    useEffect(() => {
        if (isReadOnly) {
            return;
        }

        dispatch(setSimulationSetupSelection({
            retailer: selectedRetailer?.name,
            planningLevel: "Sub-Brand + Pack Size",
            selection: selectedSubBrand?.name ?? null,
            canGenerate: canStartSimulation
        }));
    }, [
        canStartSimulation,
        dispatch,
        isReadOnly,
        selectedRetailer,
        selectedSubBrand?.name
    ]);

    useEffect(() => {
        if (!generateRequested) {
            return;
        }

        if (isReadOnly) {
            dispatch(clearGenerateSimulationRequest());
            return;
        }

        if (startInProgressRef.current || !canStartSimulation) {
            dispatch(clearGenerateSimulationRequest());
            return;
        }

        dispatch(clearGenerateSimulationRequest());
        void Promise.resolve().then(startSimulationAsync);
    }, [canStartSimulation, dispatch, generateRequested, isReadOnly, startSimulationAsync]);

    const renderBrandSubBrandSelector = (): React.ReactNode => (
        <div className="mb-3">
            <div className="row g-2 mb-1">
                <RetailerSelector
                    value={selectedRetailer}
                    retailers={retailers}
                    isFetching={status.retailers.isFetching}
                    disabled={isReadOnly || !isInitialized}
                    onChange={(retailerId: number | null): void => {
                        setSelection(prev => ({
                            ...prev,
                            packSizeKey: null,
                            retailerId
                        }));
                    }}
                />
            </div>
            <div className="row g-2 mb-1">
                <BrandSelector
                    value={selectedBrand}
                    brands={brands}
                    isFetching={status.brands.isFetching}
                    disabled={isReadOnly || !isInitialized}
                    onChange={(brandId: number | null): void => {
                        setSelection(prev => ({
                            ...prev,
                            brandId,
                            subBrandId: null,
                            packSizeKey: null
                        }));
                    }}
                />
            </div>
            <div className="row g-2 mb-1">
                <SubBrandSelector
                    brandId={selection.brandId}
                    subBrands={filteredSubBrands}
                    value={selectedSubBrand}
                    isFetching={status.subBrands.isFetching}
                    disabled={isReadOnly || !isInitialized}
                    onChange={(subBrandId: number | null): void => {
                        setSelection(prev => ({
                            ...prev,
                            subBrandId,
                            packSizeKey: null
                        }));
                    }}
                />
            </div>
            <div className="row g-2">
                <QuantitySelector
                    options={quantityOptions}
                    isFetching={status.quantities.isFetching}
                    value={selectedQuantity}
                    disabled={isReadOnly || !isInitialized}
                    onChange={(packSizeKey: string | null): void => {
                        setSelection(prev => ({
                            ...prev,
                            packSizeKey
                        }));
                    }}
                />
            </div>
        </div>
    );

    // Set the initial values based off either the AI or previous user selection.
    if (initializedToolRequestId !== toolRequest.id && status.retailers.isSuccess && status.brands.isSuccess && status.subBrands.isSuccess) {
        setInitializedToolRequestId(toolRequest.id);
        setSelection(resolveInitialSubBrandSelection({
            persistedSelection: persistedUserSelection,
            input: toolRequest.input,
            retailers,
            subBrands: allSubBrands
        }));
    }

    return (
        <div className="border rounded-3 bg-white shadow-sm">
            <div className="p-3">
                {subbrandAgentResponse === null && (
                    <>
                        <div className="mb-3">
                            <h5 className="mb-1">
                                Sub-brand simulation
                            </h5>
                            <p className="small text-body-secondary mb-0">
                                Choose a retailer, brand, sub-brand and size. All matching products will be included automatically.
                            </p>
                        </div>
                        {renderBrandSubBrandSelector()}

                        {isError && (
                            <div className="alert alert-danger py-2 px-3 small mb-0" role="alert">
                                Unable to load data.
                            </div>
                        )}
                    </>
                )}

                {selectedBrand != null && selectedSubBrand != null && selectedRetailer != null && subbrandAgentResponse == null && (
                    <div>
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                            <p className="small fw-semibold mb-0 lh-sm">
                                Matched products
                            </p>

                            <span className="badge rounded-pill text-bg-light border small">
                                {products.length}
                                {products.length === 1 ? " product included" : " products included"}
                            </span>
                        </div>

                        {(isFetching) && (
                            <div className="alert alert-light border py-2 px-3 small mb-0" role="status">
                                Loading data...
                            </div>
                        )}

                        {selectedQuantity != null && status.products.isSuccess && !status.products.isFetching && !status.products.isError && products.length === 0 && (
                            <div className="alert alert-light border py-2 px-3 small mb-0" role="status">
                                No matching products found for this sub-brand.
                            </div>
                        )}

                        {!isFetching && !isError && products.length > 0 && (
                            <div className="list-group list-group-flush border rounded-2 overflow-y-auto overflow-x-hidden" style={{ maxHeight: "360px" }}>
                                {products.map(product => {
                                    const unitLabel = getUnitLabel(product);
                                    const productMeta = getProductMeta(product);
                                    const identifiers = getProductIdentifiers(product);

                                    return (
                                        <div
                                            className="list-group-item py-1 px-2"
                                            key={product.id}
                                        >
                                            <div className="min-w-0 flex-grow-1 small lh-sm">
                                                <div className="fw-semibold text-wrap">
                                                    {product.name}
                                                </div>

                                                {productMeta && (
                                                    <div className="text-body-secondary text-wrap">
                                                        {productMeta}
                                                    </div>
                                                )}

                                                {identifiers && (
                                                    <div className="text-muted text-wrap">
                                                        {identifiers}
                                                    </div>
                                                )}
                                                {unitLabel && (
                                                    <div className="mt-1">
                                                        <span className="badge text-bg-light border text-wrap text-start">
                                                            {unitLabel}
                                                        </span>
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    );
                                })}

                            </div>
                        )}
                    </div>
                )}
                <div className="d-flex w-100">
                    <ActionButtons
                        isBusy={isChatBusy}
                        isReadOnly={isReadOnly}
                        hasStartedSimulation={hasStartedSimulation}
                        actions={actions}
                        message={message}
                        onStartSimulation={startSimulationAsync}
                        selectedSubBrand={selectedSubBrand}
                        plan={subbrandAgentResponse?.payload}
                        canStartSimulation={canStartSimulation}
                    />
                </div>
            </div>
        </div>
    );
};

export default SubBrandToolCallSteps;
